import { jwtDecode } from 'jwt-decode';

class AuthService {
  constructor(domain) {
    this.domain = domain;
    this.fetch = this.fetch.bind(this);
    this.login = this.login.bind(this);
    this.getProfile = this.getProfile.bind(this);
  }

  // Step 1 of login — returns server response (may have twoFARequired)
  async login(username, password) {
    try {
      const res = await this.fetch('/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });
      if (res.token && !res.twoFARequired) {
        this.setToken(res.token);
      }
      return res;
    } catch (err) {
      throw new Error('Login failed: ' + (err.message || 'Unknown error'));
    }
  }

  // Step 2 of login — verify 2FA code and get JWT
  async loginWith2FA(tempToken, code) {
    try {
      const res = await fetch('/login/verify-2fa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ tempToken, code }),
      });
      const data = await res.json();
      if (data.token) {
        this.setToken(data.token);
      }
      return data;
    } catch (err) {
      throw new Error('2FA verification failed: ' + (err.message || 'Unknown error'));
    }
  }

  loggedIn() {
    const token = this.getToken();
    return !!token && !this.isTokenExpired(token);
  }

  isTokenExpired(token) {
    try {
      const decoded = jwtDecode(token);
      return decoded.exp < Date.now() / 1000;
    } catch (err) {
      console.error('Token decode failed:', err);
      return true;
    }
  }

  setToken(idToken) {
    try {
      sessionStorage.setItem('token', idToken);
    } catch (err) {
      console.error('Failed to set token:', err);
    }
  }

  getToken() {
    try {
      return sessionStorage.getItem('token');
    } catch (err) {
      console.error('Failed to get token:', err);
      return null;
    }
  }

  logout() {
    try {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('tempToken');
    } catch (err) {
      console.error('Failed to remove token:', err);
    }
  }

  getProfile() {
    try {
      const token = this.getToken();
      return token ? jwtDecode(token) : null;
    } catch (err) {
      console.error('Failed to decode profile:', err);
      return null;
    }
  }

  getRole() {
    const profile = this.getProfile();
    return profile ? (profile.role || 'user') : null;
  }

  isAdmin() {
    return this.getRole() === 'admin';
  }

  setTempToken(tempToken) {
    try {
      sessionStorage.setItem('tempToken', tempToken);
    } catch (err) {
      console.error('Failed to set temp token:', err);
    }
  }

  getTempToken() {
    try {
      return sessionStorage.getItem('tempToken');
    } catch (err) {
      console.error('Failed to get temp token:', err);
      return null;
    }
  }

  clearTempToken() {
    try {
      sessionStorage.removeItem('tempToken');
    } catch (err) {
      console.error('Failed to clear temp token:', err);
    }
  }

  async fetch(url, options = {}) {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    if (this.loggedIn()) {
      headers['Authorization'] = 'Bearer ' + this.getToken();
    }

    console.log('Fetching:', url);
    try {
      const response = await fetch(url, { headers, ...options });
      return await this._checkStatus(response);
    } catch (err) {
      throw new Error('Fetch failed: ' + (err.message || 'Network error'));
    }
  }

  async _checkStatus(response) {
    if (response.status >= 200 && response.status < 300) {
      return response.json();
    }
    const error = new Error(response.statusText);
    error.response = response;
    throw error;
  }
}

const addrFull = window.location.href;
const idxSlashOne = addrFull.indexOf('//');
const idxSlashTwo = addrFull.indexOf('/', idxSlashOne + 2);
const addrHead = addrFull.substring(0, idxSlashTwo);
const authInstance = new AuthService(addrHead);
export default authInstance;
